import { useForm } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Card, CardContent } from "@/components/ui/card"
import { Dialog, DialogContent, DialogBody, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Badge } from "@/components/ui/badge"
import { Plus, Trash2, Check } from "lucide-react"
import { useState } from "react"
import { router } from "@inertiajs/react"
import type { ProjectTask } from "@/types"

type Props = {
  projectId: string
  tasks: ProjectTask[]
  users: { id: string; first_name: string; last_name: string }[]
}

export function TasksTab({ projectId, tasks, users }: Props) {
  const [showForm, setShowForm] = useState(false)

  const { data, setData, post, processing, reset } = useForm({
    title: "",
    description: "",
    due_date: "",
    assignee_id: "",
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(`/projects/${projectId}/project_tasks`, {
      onSuccess: () => {
        reset()
        setShowForm(false)
      },
    })
  }

  const toggleComplete = (task: ProjectTask) => {
    router.patch(`/projects/${projectId}/project_tasks/${task.id}`, {
      completed_at: task.completed_at ? null : new Date().toISOString(),
    })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Tâches</h3>
        <Button size="sm" onClick={() => setShowForm(!showForm)}>
          <Plus className="size-4" />
          Ajouter
        </Button>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle tâche</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="flex flex-col overflow-hidden">
            <DialogBody className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="task-title">Titre</Label>
              <Input id="task-title" value={data.title} onChange={(e) => setData("title", e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="task-description">Description</Label>
              <Textarea
                id="task-description"
                value={data.description}
                onChange={(e) => setData("description", e.target.value)}
                rows={2}
              />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="task-due-date">Date d'échéance</Label>
                <Input
                  id="task-due-date"
                  type="date"
                  value={data.due_date}
                  onChange={(e) => setData("due_date", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Assigné à</Label>
                <Select value={data.assignee_id} onValueChange={(v) => setData("assignee_id", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Non assigné" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.first_name} {u.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={processing}>
                Enregistrer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {tasks.length === 0 ? (
        <p className="text-muted-foreground text-sm">Aucune tâche</p>
      ) : (
        <div className="space-y-2">
          {tasks.map((task) => (
            <Card key={task.id}>
              <CardContent className="flex items-center gap-3 py-3">
                <Button
                  variant={task.completed_at ? "default" : "outline"}
                  size="icon"
                  className="size-7 shrink-0"
                  onClick={() => toggleComplete(task)}
                >
                  {task.completed_at && <Check className="size-4" />}
                </Button>
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-medium ${task.completed_at ? "line-through text-muted-foreground" : ""}`}>
                    {task.title}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    {task.assignee && (
                      <span>
                        {task.assignee.first_name} {task.assignee.last_name}
                      </span>
                    )}
                    {task.due_date && (
                      <Badge
                        variant={!task.completed_at && new Date(task.due_date) < new Date() ? "destructive" : "outline"}
                        className="text-xs"
                      >
                        {new Date(task.due_date).toLocaleDateString("fr-FR")}
                      </Badge>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 shrink-0"
                  onClick={() => router.delete(`/projects/${projectId}/project_tasks/${task.id}`)}
                >
                  <Trash2 className="size-3.5" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
